import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useSearchParams } from 'react-router-dom';
import {
  passwordResetRequestSchema,
  passwordResetConfirmSchema,
} from '@waw/validation-schemas';
import {
  useRequestPasswordReset,
  useConfirmPasswordReset,
} from '../../api/queries.js';
import '../auth/Login.css';

function RequestForm() {
  const [sent, setSent] = useState(false);
  const mutation = useRequestPasswordReset();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(passwordResetRequestSchema),
  });

  const onSubmit = async (data) => {
    try {
      await mutation.mutateAsync(data);
      setSent(true);
    } catch {
      // On affiche le succes meme en cas d'erreur pour ne pas reveler l'existence d'un compte
      setSent(true);
    }
  };

  if (sent) {
    return (
      <div className="card auth-card">
        <h1 className="auth-title">Email envoye</h1>
        <div className="auth-success-banner">
          Si un compte existe avec cet email, vous recevrez un lien de reinitialisation.
        </div>
        <div className="auth-footer">
          <Link to="/compte/connexion">Retour a la connexion</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="card auth-card">
      <h1 className="auth-title">Mot de passe oublie</h1>
      <p className="auth-subtitle">Entrez votre email pour recevoir un lien de reinitialisation</p>

      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <div className="form-group">
          <label className="form-label" htmlFor="reset-email">Email</label>
          <input
            id="reset-email"
            type="email"
            className={`form-input${errors.email ? ' error' : ''}`}
            placeholder="votre@email.com"
            autoComplete="email"
            {...register('email')}
          />
          {errors.email && <span className="form-error">{errors.email.message}</span>}
        </div>

        <button type="submit" className="btn btn-primary btn-block btn-lg" disabled={isSubmitting}>
          {isSubmitting ? 'Envoi...' : 'Envoyer le lien'}
        </button>
      </form>

      <div className="auth-footer">
        <Link to="/compte/connexion">Retour a la connexion</Link>
      </div>
    </div>
  );
}

function ResetForm({ token }) {
  const [done, setDone] = useState(false);
  const [serverError, setServerError] = useState('');
  const mutation = useConfirmPasswordReset();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(passwordResetConfirmSchema),
    defaultValues: { token },
  });

  const onSubmit = async (data) => {
    setServerError('');
    try {
      await mutation.mutateAsync(data);
      setDone(true);
    } catch (err) {
      if (err.status === 400) {
        setServerError('Lien invalide ou expire. Veuillez refaire une demande.');
      } else {
        setServerError(err.message || 'Une erreur est survenue.');
      }
    }
  };

  if (done) {
    return (
      <div className="card auth-card">
        <h1 className="auth-title">Mot de passe modifie</h1>
        <div className="auth-success-banner">
          Votre mot de passe a ete reinitialise avec succes.
        </div>
        <div className="auth-footer">
          <Link to="/compte/connexion">Se connecter</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="card auth-card">
      <h1 className="auth-title">Nouveau mot de passe</h1>
      <p className="auth-subtitle">Choisissez votre nouveau mot de passe</p>

      {serverError && <div className="auth-error-banner">{serverError}</div>}

      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <input type="hidden" {...register('token')} />

        <div className="form-group">
          <label className="form-label" htmlFor="new-password">Nouveau mot de passe</label>
          <input
            id="new-password"
            type="password"
            className={`form-input${errors.newPassword ? ' error' : ''}`}
            placeholder="8 caracteres minimum"
            autoComplete="new-password"
            {...register('newPassword')}
          />
          {errors.newPassword && <span className="form-error">{errors.newPassword.message}</span>}
        </div>

        <button type="submit" className="btn btn-primary btn-block btn-lg" disabled={isSubmitting}>
          {isSubmitting ? 'Enregistrement...' : 'Reinitialiser'}
        </button>
      </form>

      <div className="auth-footer">
        <Link to="/compte/connexion">Retour a la connexion</Link>
      </div>
    </div>
  );
}

export default function ForgotPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  return (
    <div className="auth-page">
      {token ? <ResetForm token={token} /> : <RequestForm />}
    </div>
  );
}
